/**
 * Coach-authored training drills (issue #461) — "push it to a player
 * alongside the existing Quick Reference guide."
 *
 * Everything here hits `/api/drills`, and every rule is enforced
 * server-side: only a coach|admin writes, only the drill's AUTHOR edits or
 * assigns it, and assignment requires an ACTIVE coach link. Nothing in
 * this module is the gate — hiding a button is a courtesy, not a control
 * (`lib/guides.js`' words) — `api/test/drills.test.ts` is where the rules
 * are proven.
 *
 * Attribution is never sent: the server takes the author and the assigner
 * from the session (#205/#190/#226), which is what makes the player's
 * "Assigned by X" label worth reading.
 */
import api from "./api";

/** The caller's OWN drills — the server scopes to the session coach; there
 * is no parameter to ask for anyone else's. */
export async function fetchMyDrills() {
  const { data } = await api.get("/drills");
  return data.drills;
}

export async function createDrill(title, body) {
  const { data } = await api.post("/drills", { title, body });
  return data;
}

/** Edit-own. The assignment is a pointer, so the edit reaches every player
 * the drill is assigned to on their next read. */
export async function updateDrill(drillId, title, body) {
  const { data } = await api.put(`/drills/${drillId}`, { title, body });
  return data;
}

/** What this player has been asked to work on. Omit `playerId` for "my
 * own" (the API defaults a player caller to their own profile); a coach
 * must pass one they are linked to — `fetchAssignedGuides`' exact shape. */
export async function fetchAssignedDrills(playerId) {
  const { data } = await api.get("/drills/assignments", {
    params: playerId ? { player_id: playerId } : undefined,
  });
  return data.assignments;
}

/** The push. 404 for another coach's drill or a player this coach cannot
 * see — the server deliberately does not distinguish them. */
export async function assignDrill(playerId, drillId) {
  const { data } = await api.post("/drills/assignments", { player_id: playerId, drill_id: drillId });
  return data;
}

/** The coach's undo — DELETE on the wire, #482's tombstone underneath, so
 * the player's bell says the drill was withdrawn instead of the card
 * silently vanishing. */
export async function withdrawDrill(assignmentId) {
  await api.delete(`/drills/assignments/${assignmentId}`);
}

/**
 * Who holds which of the coach's drills (#1159 slice 2, STOP S4, D301). No
 * read answers "who has this drill", so this is the same N-call fan-out the
 * guide assign panel uses (#481 D2): `GET /players`, then one
 * `GET /drills/assignments?player_id=` per linked player. It rejects when
 * ANY read fails, so a caller shows one honest failed state and never a
 * wrong "Not assigned yet".
 *
 * Returns `{ roster, holders }`; `holders[drill_id]` is
 * `[{ player_id, full_name, assignment_id }]` in roster order.
 */
export async function fetchDrillHolders() {
  const { data: roster } = await api.get("/players");
  const reads = await Promise.allSettled(roster.map((p) => fetchAssignedDrills(p.player_id)));
  if (reads.some((r) => r.status === "rejected")) throw new Error("A player's drills could not be read");
  const holders = {};
  roster.forEach((p, i) => {
    for (const a of reads[i].value) {
      (holders[a.drill_id] ||= []).push({ player_id: p.player_id, full_name: p.full_name, assignment_id: a.assignment_id });
    }
  });
  return { roster, holders };
}
